'use client';

import { Check, Gift, MessageSquare, X } from 'lucide-react';
import type { ExclusiveOffer, UpgradePlanName } from '@workspace/global-utils';
import { OfferCountdownTimer, OfferDiscountChip } from '@workspace/ui/components/upgrade-offer';
import { useOfferCountdown, type OfferCountdown } from '@workspace/ui/hooks/use-offer-countdown';
import { applyDiscount, formatPlanPrice } from '@workspace/ui/lib/upgrade-offer';
import { cn } from '@workspace/ui/lib/utils';

interface OfferActionProps {
  onClaim: () => void;
  onTalkToFounder: () => void;
  dataPhSelector: string;
}

const surfaceClass = (urgent: boolean) =>
  urgent ? 'bg-offer-urgent shadow-offer-urgent' : 'bg-offer shadow-offer';

const accentClass = (urgent: boolean) => (urgent ? 'bg-offer-amber' : 'bg-offer-gold');

const formatCredits = (credits: number) => credits.toLocaleString('en-US');

function OfferButton({
  urgent,
  onClick,
  dataPhSelector,
  className,
  children,
}: {
  urgent: boolean;
  onClick: () => void;
  dataPhSelector: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-ph-selector={dataPhSelector}
      className={cn(
        'inline-flex h-8 shrink-0 cursor-pointer items-center justify-center gap-1.5 rounded-lg px-3.5 text-[13px] font-bold text-offer-ink transition-[filter] duration-150 hover:brightness-105',
        accentClass(urgent),
        className,
      )}
    >
      {children}
    </button>
  );
}

function FounderButton({
  onTalkToFounder,
  dataPhSelector,
}: Pick<OfferActionProps, 'onTalkToFounder' | 'dataPhSelector'>) {
  return (
    <OfferButton
      urgent={false}
      onClick={onTalkToFounder}
      dataPhSelector={`${dataPhSelector}-talk-to-founder`}
    >
      <MessageSquare className="size-3.5" aria-hidden="true" />
      Talk to founder
    </OfferButton>
  );
}

function DismissButton({
  onDismiss,
  dataPhSelector,
  className,
}: {
  onDismiss: () => void;
  dataPhSelector: string;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onDismiss}
      data-ph-selector={`${dataPhSelector}-dismiss`}
      className={cn(
        'grid size-6 shrink-0 cursor-pointer place-items-center rounded text-white/70 transition-[color] duration-150 hover:text-white',
        className,
      )}
    >
      <X className="size-4" aria-hidden="true" />
      <span className="sr-only">Dismiss offer</span>
    </button>
  );
}

function OfferTag({ urgent, children }: { urgent?: boolean; children: React.ReactNode }) {
  return (
    <span
      className={cn(
        'shrink-0 rounded-md px-2 py-1 text-[11px] font-bold tracking-wider uppercase',
        urgent === undefined
          ? 'border border-white/30 text-white'
          : urgent
            ? 'border border-white/40 text-white'
            : 'bg-offer-gold text-offer-ink',
      )}
    >
      {children}
    </span>
  );
}

function OfferBarContent({
  offer,
  countdown,
  onClaim,
  onTalkToFounder,
  dataPhSelector,
}: OfferActionProps & { offer: ExclusiveOffer; countdown: OfferCountdown }) {
  const { discountPercent, creditsReward } = offer;

  if (countdown.phase === 'ended') {
    return (
      <>
        <OfferTag>{formatCredits(creditsReward)} free</OfferTag>
        <p className="min-w-0 text-[13px] text-white/75">
          <b className="font-bold text-white">Get {formatCredits(creditsReward)} credits free.</b>{' '}
          Just talk to a founder.
        </p>
        <FounderButton onTalkToFounder={onTalkToFounder} dataPhSelector={dataPhSelector} />
      </>
    );
  }

  const urgent = countdown.phase === 'last_call';
  return (
    <>
      <OfferTag urgent={urgent}>{urgent ? 'Countdown' : `${discountPercent}% off`}</OfferTag>
      <p className="min-w-0 text-[13px] font-bold text-white">
        {urgent
          ? `Last call. ${discountPercent}% off ends in`
          : `You unlocked ${discountPercent}% off every plan. Ends in`}
      </p>
      <OfferCountdownTimer
        countdown={countdown}
        className={cn('rounded-lg px-2.5 py-1 text-[13px]', urgent ? 'bg-black/30' : 'bg-white/16')}
      />
      <OfferButton urgent={urgent} onClick={onClaim} dataPhSelector={`${dataPhSelector}-claim`}>
        {urgent ? 'Claim now' : `Claim ${discountPercent}% off`}
      </OfferButton>
    </>
  );
}

function OfferModalContent({
  offer,
  countdown,
  onTalkToFounder,
  dataPhSelector,
}: Omit<OfferActionProps, 'onClaim'> & { offer: ExclusiveOffer; countdown: OfferCountdown }) {
  const ended = countdown.phase === 'ended';
  const urgent = countdown.phase === 'last_call';
  const TileIcon = ended ? Gift : Check;

  return (
    <div className="flex flex-wrap items-center gap-x-3.5 gap-y-2">
      <span
        className={cn(
          'grid size-7 shrink-0 place-items-center rounded-lg text-offer-ink',
          accentClass(urgent),
          urgent ? 'shadow-offer-chip-urgent' : 'shadow-offer-chip',
        )}
      >
        <TileIcon className="size-4" aria-hidden="true" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[15px] font-bold leading-[21px] text-white">
          {ended
            ? `Get ${formatCredits(offer.creditsReward)} credits free.`
            : urgent
              ? `Last call: ${offer.discountPercent}% off ends soon`
              : `${offer.discountPercent}% off applied to every plan`}
        </p>
        <p className="text-[13px] leading-[18px] text-white/75">
          {ended
            ? 'Spend 15 minutes with a founder and tell us what you are building.'
            : 'Prices below already include the discount.'}
        </p>
      </div>
      {ended ? (
        <FounderButton onTalkToFounder={onTalkToFounder} dataPhSelector={dataPhSelector} />
      ) : (
        <OfferCountdownTimer countdown={countdown} className="text-[13px]" />
      )}
    </div>
  );
}

// One banner for every offer surface: the phase picks the content and theme, the variant the layout.
export function ExclusiveOfferBanner({
  offer,
  variant,
  onClaim,
  onTalkToFounder,
  onDismiss,
  dataPhSelector,
  className,
}: OfferActionProps & {
  offer: ExclusiveOffer;
  variant: 'bar' | 'inline' | 'modal';
  onDismiss?: () => void;
  className?: string;
}) {
  const countdown = useOfferCountdown(offer);
  const urgent = countdown.phase === 'last_call';

  if (variant === 'modal') {
    return (
      <div className={cn('offer-shine rounded-[14px] px-3.5 py-[11px]', surfaceClass(urgent))}>
        <OfferModalContent
          offer={offer}
          countdown={countdown}
          onTalkToFounder={onTalkToFounder}
          dataPhSelector={dataPhSelector}
        />
      </div>
    );
  }

  return (
    <div
      data-ph-selector={dataPhSelector}
      className={cn(
        'offer-shine flex flex-wrap items-center gap-x-3 gap-y-1.5',
        variant === 'bar'
          ? 'min-h-[46px] justify-center px-10 py-1.5'
          : 'rounded-[14px] px-3 py-2 [&>p]:flex-1',
        surfaceClass(urgent),
        className,
      )}
    >
      <OfferBarContent
        offer={offer}
        countdown={countdown}
        onClaim={onClaim}
        onTalkToFounder={onTalkToFounder}
        dataPhSelector={dataPhSelector}
      />
      {onDismiss && (
        <DismissButton
          onDismiss={onDismiss}
          dataPhSelector={dataPhSelector}
          className={variant === 'bar' ? 'absolute top-2.5 right-3.5' : undefined}
        />
      )}
    </div>
  );
}

// The card version inside feature-gate modals, priced for the plan that unlocks the feature.
export function ExclusiveOfferCard({
  offer,
  plan,
  annualPrice,
  currency = 'USD',
  countdown,
}: {
  offer: ExclusiveOffer;
  plan: UpgradePlanName;
  annualPrice: { current: number; annualTotal?: number };
  currency?: string;
  countdown: OfferCountdown;
}) {
  const urgent = countdown.phase === 'last_call';
  const { current, annualTotal } = annualPrice;
  const discounted = applyDiscount(current, offer.discountPercent);

  return (
    <div
      className={cn(
        'offer-shine flex flex-col gap-2.5 rounded-[12.6px] px-2.5 py-[13px]',
        surfaceClass(urgent),
      )}
    >
      <div className="flex items-center gap-[11px]">
        <span
          className={cn(
            'grid size-8 shrink-0 place-items-center rounded-[9px] text-offer-ink',
            accentClass(urgent),
            urgent ? 'shadow-offer-chip-urgent' : 'shadow-offer-chip',
          )}
        >
          <Gift className="size-4" aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[13.5px] font-bold leading-[19px] text-white">
            {plan} at {offer.discountPercent}% off, applied
          </p>
          <p className="text-[11.7px] leading-4 text-white/75">
            <s className="opacity-70">{formatPlanPrice(current, currency)}/mo</s>{' '}
            {formatPlanPrice(discounted, currency)}/mo
            {annualTotal !== undefined &&
              ` · billed yearly at ${formatPlanPrice(applyDiscount(annualTotal, offer.discountPercent), currency)}`}
          </p>
        </div>
        <OfferDiscountChip
          discountPercent={offer.discountPercent}
          urgent={urgent}
          className={urgent ? 'shadow-offer-chip-urgent' : 'shadow-offer-chip'}
        />
      </div>
      <div className="mx-[3px] flex items-center gap-2 rounded-[9px] bg-white/15 py-[5px] ps-[11px] pe-[7px]">
        <span className="text-[11px] text-white/75">Ends in</span>
        <OfferCountdownTimer countdown={countdown} className="text-[12.6px]" />
      </div>
    </div>
  );
}
